import type { CouponHistoryResponse } from '../../types/member.js';
import { useTranslation } from 'react-i18next';

interface CouponTabProps {
  data: CouponHistoryResponse;
}

/** @graph-connects none */
export function CouponTab({ data }: CouponTabProps) {
  const { t } = useTranslation();

  if (data.coupons.length === 0) {
    return (
      <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] py-12 text-center text-sm text-[var(--color-text-muted)]">
        {t('member.noCouponHistory')}
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)]">
      <div className="border-b border-[var(--color-border)] px-4 py-3 text-[13px] font-semibold text-[var(--color-text-primary)]">
        {t('member.couponHistory')}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--color-border)] bg-[var(--color-bg-elevated)]">
              <th className="px-3.5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-secondary)]">
                {t('member.claimedDate')}
              </th>
              <th className="px-3.5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-secondary)]">
                {t('member.couponName')}
              </th>
              <th className="px-3.5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-secondary)]">
                {t('member.discountDescription')}
              </th>
              <th className="px-3.5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-secondary)]">
                {t('common.status')}
              </th>
              <th className="px-3.5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-secondary)]">
                {t('member.usedDate')}
              </th>
            </tr>
          </thead>
          <tbody>
            {data.coupons.map((coupon, i) => (
              <tr key={i} className="border-b border-[var(--color-border)]">
                <td className="whitespace-nowrap px-3.5 py-2.5 font-mono text-[12px]">
                  {formatDate(coupon.claimedAt)}
                </td>
                <td className="px-3.5 py-2.5 text-[13px]">{coupon.couponName}</td>
                <td className="px-3.5 py-2.5 text-[13px] text-[var(--color-text-secondary)]">
                  {coupon.discountDescription}
                </td>
                <td className="whitespace-nowrap px-3.5 py-2.5">
                  <span
                    className="inline-block rounded-full px-2 py-0.5 text-[11px] font-medium"
                    style={{
                      background:
                        coupon.status === 'pending_payment'
                          ? 'var(--color-warning-bg)'
                          : coupon.status === 'paid' || coupon.usedAt
                            ? 'var(--color-bg-elevated)'
                            : 'var(--color-success-bg)',
                      color:
                        coupon.status === 'pending_payment'
                          ? 'var(--color-warning)'
                          : coupon.status === 'paid' || coupon.usedAt
                            ? 'var(--color-text-muted)'
                            : 'var(--color-success)',
                    }}
                  >
                    {coupon.status === 'pending_payment'
                      ? t('member.couponPending')
                      : coupon.status === 'paid' || coupon.usedAt
                        ? t('member.couponUsed')
                        : t('member.couponUnused')}
                  </span>
                </td>
                <td className="whitespace-nowrap px-3.5 py-2.5 font-mono text-[12px]">
                  {coupon.usedAt ? formatDate(coupon.usedAt) : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** @graph-connects none */
function formatDate(isoDate: string): string {
  const _d = new Date(isoDate);
  if (Number.isNaN(_d.getTime())) return isoDate;
  return _d.toLocaleDateString('ja-JP', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}
